import { useEffect, useState } from 'react';
import { toast } from "react-hot-toast";
import { getBatchStudentsByBatchIdApi } from '../../../../Api/BatchStudent';
import { useNavigate } from "react-router-dom";
import { formatDate } from '../../../general/FormatDateTime';

type Props = {
    url: string,
    batchId: string,
};

type BatchStudents = {
    id: string;
    batchId: string;
    studentId: string;
    user: UserInterface;
    paidOn: string;
    paidTill: string;
};

type UserInterface = {
    id: string;
    name: string;
    email: string;
    mobile: string;
    gender: number | null;
    address: string;
    roleType: string;
};

const BatchStudents = ({ url, batchId }: Props) => {
    const [loader, setLoader] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const itemsPerPage = 10;
    const [Students, setStudents] = useState<BatchStudents[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>("");
    const navigate = useNavigate();

    const handleApiCallClick = async () => {
        try {
            setLoader(true);
            const response = await getBatchStudentsByBatchIdApi(url, batchId, currentPage, itemsPerPage);
            setStudents(response.students); // Use the correct key from the API response
            setTotalPages(Math.ceil(response.totalCount / itemsPerPage)); // Use the correct key for total count
        } catch (error) {
            toast.error("Failed to fetch data. Some problem occurred. Please try again.");
        } finally {
            setLoader(false);
        }
    };

    useEffect(() => {
        handleApiCallClick();
    }, [currentPage]);

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value.toLowerCase());
    };

    const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setCurrentPage(1);
        handleApiCallClick();
    };

    const handleEditClick = (studentId: string) => {
        navigate("/admin/StudentForm", { state: { studentId } });
    };


    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage((prev) => prev + 1);
        }
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage((prev) => prev - 1);
        }
    };



    return (
        <div className="container mx-auto mt-6">
            <h2 className="text-3xl font-bold text-blue-600 mb-5">Students</h2>

            {/* Search */}
            <form onSubmit={handleSearchSubmit} className="w-full md:flex-grow mb-4">
                <label
                    htmlFor="default-search"
                    className="mb-2 text-sm font-medium text-gray-900 sr-only dark:text-white"
                >
                    Search
                </label>
                <div className="relative">
                    <div className="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
                        <svg
                            className="w-4 h-4 text-gray-500 dark:text-gray-400"
                            aria-hidden="true"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 20 20"
                        >
                            <path
                                stroke="currentColor"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"
                            />
                        </svg>
                    </div>
                    <input
                        type="search"
                        id="default-search"
                        className="w-full p-3 ps-10 outline-none text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                        placeholder="Search by Name, Email, Mobile"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        required
                    />
                    <button
                        type="submit"
                        className="text-white absolute end-1.5 bottom-1.5 bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-4 py-2 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
                    >
                        Search
                    </button>
                </div>
            </form>

            {/* Table */}
            <div className="overflow-x-auto mt-5">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 md:rounded-lg">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                        <tr>
                            <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">Action</th>
                            <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">Name</th>
                            <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">Email</th>
                            <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">Mobile</th>
                            <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">Gender</th>
                            <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">Role</th>
                            <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">Paid On</th>
                            <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">Paid Till</th>
                        </tr>
                    </thead>

                    <tbody className="bg-white divide-y divide-gray-200 dark:divide-gray-700 dark:bg-gray-900">
                        {loader ? (
                            <tr className="text-center">
                                <td colSpan={6} className="py-3 text-center">
                                    <div className="w-12 h-12 border-4 text-center border-t-blue-500 border-gray-300 rounded-full animate-spin mx-auto"></div>
                                </td>
                            </tr>
                        ) : Students.length === 0 ? (
                            <tr>
                                <td colSpan={8} className="py-3 text-center text-gray-500">No students found.</td>
                            </tr>
                        )
                            : (
                                Students?.map((item) => (
                                    <tr key={item.id} className="hover:bg-gray-50">
                                        <td className="px-4 py-4 text-sm whitespace-nowrap">
                                            <div className="flex items-center gap-x-3">
                                                <button
                                                    onClick={() => handleEditClick(item.studentId)}
                                                    className="text-gray-500 transition-colors duration-200 dark:hover:text-yellow-500 dark:text-gray-300 hover:text-yellow-500 focus:outline-none"
                                                >
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        strokeWidth="1.5"
                                                        stroke="currentColor"
                                                        className="w-5 h-5"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
                                                        />
                                                    </svg>
                                                </button>
                                            </div>
                                        </td>
                                        <td className="px-4 py-4 text-sm font-normal text-gray-700 whitespace-nowrap">{item.user.name}</td>
                                        <td className="px-4 py-4 text-sm font-normal text-gray-700 whitespace-nowrap">{item.user.email}</td>
                                        <td className="px-4 py-4 text-sm font-normal text-gray-700 whitespace-nowrap">{item.user.mobile}</td>
                                        <td className="px-4 py-4 text-sm font-normal text-gray-700 whitespace-nowrap">{item.user.gender === 1 ? "Male" : item.user.gender === 2 ? "Female" : "Other"}</td>
                                        <td className="px-4 py-4 text-sm font-normal text-gray-700 whitespace-nowrap">{item.user.roleType}</td>
                                        <td className="px-4 py-4 text-sm font-normal text-gray-700 whitespace-nowrap">{formatDate(item.paidOn)}</td>
                                        <td className="px-4 py-4 text-sm font-normal text-gray-700 whitespace-nowrap">{formatDate(item.paidTill)}</td>
                                    </tr>
                                ))
                            )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            <div className="flex flex-wrap items-center justify-center md:justify-between mt-6 gap-2 md:gap-4">
                <button
                    disabled={currentPage === 1}
                    onClick={handlePreviousPage}
                    className="flex items-center px-5 py-2 text-sm text-gray-700 capitalize transition-colors duration-200 bg-white border rounded-md gap-x-2 hover:bg-gray-100 dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:hover:bg-gray-800"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        className="w-5 h-5 rtl:-scale-x-100"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M6.75 15.75L3 12m0 0l3.75-3.75M3 12h18"
                        />
                    </svg>
                    <span>Previous</span>
                </button>
                <div className="flex flex-wrap items-center justify-center gap-x-2">
                    {[...Array(Math.max(totalPages, 1))].map((_, index) => (
                        <button
                            key={index + 1}
                            onClick={() => setCurrentPage(index + 1)}
                            className={`px-2 py-1 text-sm ${currentPage === index + 1
                                ? "text-blue-500 bg-blue-100"
                                : "text-gray-500 hover:bg-gray-100"
                                } rounded-md`}
                        >
                            {index + 1}
                        </button>
                    ))}
                </div>
                <button
                    disabled={currentPage === totalPages}
                    onClick={handleNextPage}
                    className="flex items-center px-5 py-2 text-sm text-gray-700 capitalize transition-colors duration-200 bg-white border rounded-md gap-x-2 hover:bg-gray-100 dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:hover:bg-gray-800"
                >
                    <span>Next</span>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        className="w-5 h-5 rtl:-scale-x-100"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3"
                        />
                    </svg>
                </button>
            </div>
        </div>
    );
};

export default BatchStudents;